import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, ArrowUpRight, Instagram, Facebook } from "lucide-react";
import Logo, { Mark } from "./Logo";
import { COMPANY, SERVICES } from "../lib/site";

const Footer = () => (
  <footer className="border-t border-white/10 bg-[#070f1d]" data-testid="site-footer">
    <div className="container-ek grid gap-14 py-20 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr]">
      <div>
        <Logo />
        <p className="mt-6 max-w-sm text-sm leading-relaxed text-mist">
          A premium home improvement and construction company serving London &
          the South East. Every project managed end-to-end, every finish
          guaranteed.
        </p>
        <div className="mt-8 flex items-center gap-3">
          <Mark className="h-8 w-8 opacity-60" />
          <p className="text-[11px] uppercase tracking-[0.25em] text-slate-500">
            Company No. {COMPANY.reg}
          </p>
        </div>
      </div>

      <nav data-testid="footer-services-nav">
        <p className="eyebrow mb-6">Services</p>
        <ul className="space-y-3">
          {SERVICES.map((s) => (
            <li key={s.slug}>
              <Link
                to={`/services/${s.slug}`}
                data-testid={`footer-service-${s.slug}`}
                className="group flex items-center gap-2 text-sm text-white/70 transition-colors hover:text-gold"
              >
                {s.name}
                <ArrowUpRight className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <nav data-testid="footer-company-nav">
        <p className="eyebrow mb-6">Company</p>
        <ul className="space-y-3">
          {[
            { to: "/", label: "Home", id: "home" },
            { to: "/about", label: "About Us", id: "about" },
            { to: "/gallery", label: "Gallery", id: "gallery" },
            { to: "/contact", label: "Contact & Quotes", id: "contact" },
          ].map((l) => (
            <li key={l.id}>
              <Link
                to={l.to}
                data-testid={`footer-${l.id}`}
                className="text-sm text-white/70 transition-colors hover:text-gold"
              >
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <div data-testid="footer-contact-info">
        <p className="eyebrow mb-6">Get In Touch</p>
        <ul className="space-y-4 text-sm text-white/70">
          <li className="flex items-center gap-3">
            <MapPin className="h-4 w-4 shrink-0 text-gold" />
            <span>{COMPANY.area}</span>
          </li>
          <li className="flex items-center gap-3">
            <Phone className="h-4 w-4 shrink-0 text-gold" />
            <a href={COMPANY.phoneHref} data-testid="footer-phone" className="transition-colors hover:text-gold">
              {COMPANY.phone}
            </a>
          </li>
          <li className="flex items-center gap-3">
            <Mail className="h-4 w-4 shrink-0 text-gold" />
            <a href={`mailto:${COMPANY.email}`} data-testid="footer-email" className="transition-colors hover:text-gold">
              {COMPANY.email}
            </a>
          </li>
          <li className="flex items-center gap-3">
            <Clock className="h-4 w-4 shrink-0 text-gold" />
            <span>{COMPANY.hours}</span>
          </li>
        </ul>
        <div className="mt-8 flex items-center gap-3" data-testid="footer-socials">
          <p className="text-[10px] uppercase tracking-[0.25em] text-mist">Follow Us</p>
          <a
            href={COMPANY.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-social-instagram"
            aria-label="Esskay Homes on Instagram"
            className="flex h-10 w-10 items-center justify-center border border-white/15 text-white/80 transition-all duration-300 hover:border-gold hover:text-gold"
          >
            <Instagram className="h-4 w-4" />
          </a>
          <a
            href={COMPANY.facebook}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-social-facebook"
            aria-label="Esskay Homes on Facebook"
            className="flex h-10 w-10 items-center justify-center border border-white/15 text-white/80 transition-all duration-300 hover:border-gold hover:text-gold"
          >
            <Facebook className="h-4 w-4" />
          </a>
        </div>
      </div>
    </div>

    <div className="border-t border-white/10">
      <div className="container-ek flex flex-col items-center justify-between gap-3 py-7 text-[11px] uppercase tracking-[0.2em] text-slate-500 md:flex-row">
        <p className="leading-relaxed">
          © {new Date().getFullYear()} {COMPANY.legalName} · Registered in England & Wales ·
          Company No. {COMPANY.reg}
          <span className="mt-1.5 block text-[10px] normal-case tracking-normal text-slate-600">
            Registered address: {COMPANY.address}
          </span>
        </p>
        <p className="flex items-center gap-2">
          <span className="h-1 w-1 rotate-45 bg-gold/70" />
          12-Month Workmanship Warranty
        </p>
      </div>
    </div>
  </footer>
);

export default Footer;
